import { Card, Badge } from 'react-bootstrap';
import { format, Locale, isValid } from 'date-fns';
import { ja, enUS, zhCN, fr, es, de, pt, ru } from 'date-fns/locale';
import { useTranslation } from 'react-i18next';
import { BillingSubscription } from '../../services/billing.service';

interface SubscriptionCardProps {
  subscription: BillingSubscription;
}

export default function SubscriptionCard({ subscription }: SubscriptionCardProps) {
  const { t, i18n } = useTranslation();

  const getStatusVariant = (status: string) => {
    switch (status.toLowerCase()) {
      case 'active':
        return 'success';
      case 'canceled':
        return 'danger';
      case 'past_due':
        return 'warning';
      default:
        return 'secondary';
    }
  };

  const formatDate = (dateString?: string | null) => {
    if (!dateString) {
      return '-';
    }
    const date = new Date(dateString);
    if (!isValid(date)) {
      return '-';
    }

    const currentLang = i18n.resolvedLanguage || i18n.language || 'ja';
    const localeMap: Record<string, Locale> = {
      ja: ja,
      en: enUS,
      'zh-CN': zhCN,
      zh: zhCN,
      fr: fr,
      es: es,
      de: de,
      pt: pt,
      ru: ru,
    };
    const locale = localeMap[currentLang] || localeMap[currentLang.split('-')[0]] || enUS;
    const formatPattern = currentLang === 'ja' ? 'yyyy/MM/dd' : 'yyyy-MM-dd';

    return format(date, formatPattern, { locale });
  };

  const getStatusLabel = (status: string) => {
    const normalized = status.toLowerCase();
    const key = `billing.subscription_status_${normalized}`;
    return t(key, { defaultValue: status });
  };

  return (
    <Card className="mb-3">
      <Card.Body>
        <div className="d-flex justify-content-between align-items-start mb-2">
          <Card.Title className="mb-0">{subscription.plan}</Card.Title>
          <Badge bg={getStatusVariant(subscription.status)}>
            {getStatusLabel(subscription.status)}
          </Badge>
        </div>
        {subscription.currentPeriodStart && subscription.currentPeriodEnd && (
          <div className="text-muted small mb-2">
            <div>
              {t('billing.subscription_period', { defaultValue: '期間' })}:{' '}
              {formatDate(subscription.currentPeriodStart)} -{' '}
              {formatDate(subscription.currentPeriodEnd)}
            </div>
          </div>
        )}
        {subscription.cancelAtPeriodEnd && (
          <Badge bg="warning" className="mt-2">
            {t('billing.subscription_cancel_at_period_end', {
              defaultValue: '期間終了時にキャンセル予定',
            })}
          </Badge>
        )}
      </Card.Body>
    </Card>
  );
}
